import clsx from "clsx";
import { isEmpty } from "lodash";
import { Box, Typography, Grid, Button, useMediaQuery, useTheme } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { toastr } from "react-redux-toastr";
import React, { useState, useCallback, useRef } from "react";

// styles
import "../style.scss";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

const RenewalOptions = ({
  values,
  currency,
  selectedValue,
  setValue,
  getRenewalError,
  formInfo,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const getOpenAmount = values?.find((el) => el?.openAmount);
  // exclude only isPledge entries — the Custom button handles those
  const getAmounts = values?.filter((el) => el?.amount > 0 && !el?.isPledge && !el?.openAmount);

  const [isAddingOther, setIsAddingOther] = useState(false);
  const [otherAmount, setOtherAmount] = useState("");
  // remember last entered value so re-editing pre-fills it
  const lastEnteredRef = useRef("");

  const isSelected = (el) => el?.amount === selectedValue;
  const isPledge = values?.some((el) => el?.isPledge === true);
  const currentPledge = values?.find((el) => el?.isPledge);

  const lastYear = Number(formInfo?.lastYearForCalculation || 0);

  const handleSaveOther = useCallback(() => {
    const total = Number(otherAmount);
    if (!otherAmount || isNaN(total) || total <= 0) return;

    const isAmountAlready = values
      ?.filter((el) => !el?.isPledge && !el?.openAmount)
      .some((el) => el?.amount === total);
    if (isAmountAlready) {
      toastr.error("Error", "This amount matches an existing option — please pick it from above.");
      return;
    }

    const newOption = {
      ...getOpenAmount,
      amount: total,
      isPledge: true,
    };
    const updatedList = [...values?.filter((el) => !el?.isPledge), newOption];
    lastEnteredRef.current = otherAmount;

    setValue("formInfo.membershipPricing", updatedList);
    setValue("renewalOptions", newOption?.amount);
    setIsAddingOther(false);
    setOtherAmount("");
  }, [values, otherAmount, getOpenAmount, setValue]);

  const handleUncheck = () => {
    const getValue = values?.filter((el) => !el?.isPledge);
    setValue("formInfo.membershipPricing", getValue);
    setValue("renewalOptions", null);
    setIsAddingOther(false);
    setOtherAmount("");
    lastEnteredRef.current = "";
  };

  const handleCheckboxChange = (e) => {
    if (!e.target.checked) {
      handleUncheck();
    } else {
      // pre-fill with last entered value, or fall back to the suggested open amount
      const prefill = lastEnteredRef.current || (getOpenAmount?.amount ? String(getOpenAmount.amount) : "");
      setOtherAmount(prefill);
      setIsAddingOther(true);
    }
  };

  return (
    <Box className="summary-renewal__card">
      <Typography className="summary-renewal__title">
        Renewal Options
      </Typography>

      {!isEmpty(getAmounts) && (
        <Typography className="summary-renewal__desc">
          {formInfo?.message && formInfo.message.trim() !== ""
            ? formInfo.message
            : "We encourage you to consider a higher dues sponsorship level, as this helps us to have special programs and enhanced holiday celebrations."}
        </Typography>
      )}

      {/* Renewal options + Custom button in one grid */}
      <Grid container spacing={2} className="summary-renewal__options">
        {getAmounts?.map((el, i) => {
          const customIsActive = isPledge && selectedValue === currentPledge?.amount;
          const selected = isSelected(el) && !customIsActive;
          return (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Button
                fullWidth
                variant="outlined"
                style={{ minHeight: 56, height: "auto", padding: "8px 12px" }}
                className={clsx(
                  "summary-renewal__option-button",
                  "desc",
                  selected && "selected"
                )}
                onClick={() => {
                  if (isPledge) handleUncheck();
                  setValue("renewalOptions", el?.amount);
                }}
              >
                <Box display="flex" flexDirection="column" alignItems={isMobile ? "flex-start" : "center"} width="100%">
                  <span style={{ fontSize: Fonts.GeneralSmallHeaderFont.fontSize, fontWeight: Fonts.GeneralSmallHeaderFont.fontWeight, color: el?.isRequired ? "#2455ef85" : selected ? "#155eef" : "rgb(65 73 84)" }}>
                    {el?.isPledge ? "Custom" : (el?.title ?? "Option")}
                  </span>
                  <span style={{ fontSize: Fonts.GeneralLabelFont.fontSize, fontWeight: Fonts.GeneralLabelFont.fontWeight, color: el?.isRequired ? "#2455ef85" : selected ? "#155eef" : "rgb(65 73 84)" }}>
                    {`${currency}${Number(el?.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </span>
                </Box>
              </Button>
            </Grid>
          );
        })}

        {/* Custom amount button — inside same grid for equal spacing */}
        {getOpenAmount && (
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="outlined"
              style={{ minHeight: 56, height: "auto", padding: "8px 12px" }}
              className={clsx(
                "summary-renewal__option-button",
                "desc",
                (isAddingOther || (isPledge && selectedValue === currentPledge?.amount)) && "selected"
              )}
              onClick={() => {
                if (isAddingOther) {
                  setIsAddingOther(false);
                  if (!isPledge) setOtherAmount("");
                } else if (isPledge) {
                  setOtherAmount(String(currentPledge.amount));
                  lastEnteredRef.current = String(currentPledge.amount);
                  setIsAddingOther(true);
                } else {
                  handleCheckboxChange({ target: { checked: true } });
                }
              }}
            >
              <Box display="flex" flexDirection="column" alignItems={isMobile ? "flex-start" : "center"} width="100%">
                <>
                  <span style={{ fontSize: Fonts.GeneralInputFont.fontSize, fontWeight: FontWeights.Bold, color: (isAddingOther || (isPledge && selectedValue === currentPledge?.amount)) ? "#155eef" : "rgb(65 73 84)" }}>
                    {getOpenAmount?.title}
                  </span>
                  <span style={{ fontSize: Fonts.GeneralCaptionFont.fontSize, fontWeight: Fonts.GeneralCaptionFont.fontWeight, color: (isAddingOther || (isPledge && selectedValue === currentPledge?.amount)) ? "#155eef" : "rgb(65 73 84)" }}>
                    (Open Amount)
                  </span>
                  {isPledge && currentPledge && (
                    <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
                      <span style={{ fontSize: Fonts.GeneralInputFont.fontSize, fontWeight: Fonts.GeneralInputFont.fontWeight, color: (isPledge && selectedValue === currentPledge?.amount) ? "#155eef" : "rgb(65 73 84)" }}>
                        {`${currency}${Number(currentPledge.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </span>
                      <EditIcon style={{ fontSize: 14, opacity: 0.7 }} />
                    </Box>
                  )}
                </>
              </Box>
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Error message */}
      <Box className="summary-renewal__error">
        {getRenewalError && (
          <Typography color="error" style={{ marginTop: 10 }}>
            {getRenewalError}
          </Typography>
        )}
      </Box>

      {/* Empty state */}
      {isEmpty(getAmounts) && !getOpenAmount && (
        <Box
          sx={{
            flexDirection: "column",
            height: 100,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Typography variant="h6">No data available.</Typography>
        </Box>
      )}

      {/* Input row — shown below grid when custom is active */}
      {getOpenAmount && isAddingOther && (
        <Box display="flex" alignItems="center" mt={1} style={{ gap: 8 }}>
          <Box display="flex" alignItems="center" style={{ background: "#f3f4f6", borderRadius: 4, height: 48, paddingLeft: 12, paddingRight: 12, width: 220, boxSizing: "border-box" }}>
            <span style={{ ...Fonts.GeneralLargeBodyFont, color: FontColors.Slate344054, marginRight: 2, userSelect: "none" }}>
              {currency}
            </span>
            <input
              type="text"
              placeholder={lastYear > 0 ? Number(lastYear + 500).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}
              value={otherAmount}
              onChange={(e) => setOtherAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveOther(); } }}
              autoFocus
              style={{ border: "none", outline: "none", flex: 1, ...Fonts.GeneralLargeBodyFont, color: FontColors.Slate344054, background: "transparent", width: "100%" }}
            />
          </Box>
          <button
            onClick={handleSaveOther}
            disabled={!otherAmount || isNaN(Number(otherAmount)) || Number(otherAmount) <= 0}
            style={{
              height: 48,
              padding: "8px 20px",
              borderRadius: 5,
              border: 0,
              background: (!otherAmount || isNaN(Number(otherAmount)) || Number(otherAmount) <= 0) ? "var(--Gray-20, #F0F0F3)" : "var(--blues-blue-main, #2555EF)",
              color: (!otherAmount || isNaN(Number(otherAmount)) || Number(otherAmount) <= 0) ? "var(--Gray-100, #B5B5C3)" : "#fff",
              ...Fonts.GeneralButtonFont,
              cursor: (!otherAmount || isNaN(Number(otherAmount)) || Number(otherAmount) <= 0) ? "not-allowed" : "pointer",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            Confirm
          </button>
        </Box>
      )}
    </Box>
  );
};

export default RenewalOptions;
